import { API_BASE } from '../lib/apiOrigin';
import React, { useState } from "react";
import axios from "axios";
import { X, Send, MessageCircleQuestion } from "lucide-react";

const API = API_BASE;

// Pre-built question chips. Clicking one appends to the textarea so admin
// can chain several common asks without typing each from scratch.
const PROMPTS = [
  { label: "Product model", text: "Could you share the exact product model or brand?" },
  { label: "Product size", text: "What are the dimensions of the product (length × width × height)?" },
  { label: "Photos", text: "Please send a few photos of the install area and the product (or its box)." },
  { label: "Delivery status", text: "Has the product been delivered yet, or do you have a target delivery date?" },
  { label: "Install surface", text: "What type of surface will the structure be installed on (concrete, grass, deck, paver, etc.)?" },
  { label: "Access issues", text: "Are there any access constraints we should know about (narrow gates, stairs, slope, etc.)?" },
  { label: "Preferred date", text: "Do you have a preferred installation date or time window?" },
  { label: "Anchoring", text: "Would you like the structure anchored to the ground? If so, how many anchor points (4 or 6)?" },
  { label: "Blinds / LED / pallets", text: "Will the project include blinds, LED lights, or pallet removal? Quantities if applicable?" },
];

const AskQuestionsDialog = ({ open, onOpenChange, lead, token, onSent }) => {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("needs_info");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!open) return null;

  const reset = () => {
    setMessage("");
    setStatus("needs_info");
    setError("");
    setSuccess("");
    setSending(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const insertPrompt = (text) => {
    setMessage((m) => (m ? `${m.trimEnd()}\n\n${text}` : text));
  };

  const handleSend = async () => {
    if (!message.trim() || message.trim().length < 4) {
      setError("Please write at least a few words.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/admin/leads/${lead.id}/ask-questions`,
        { message: message.trim(), status },
        { headers: { "X-Admin-Token": token } }
      );
      setSuccess(`Sent to ${res.data?.sent_to}. Lead → ${res.data?.status}.`);
      if (onSent) onSent(res.data);
      setTimeout(close, 1400);
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Failed to send the question email."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="ask-questions-overlay"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        data-testid="ask-questions-dialog"
        className="w-full sm:max-w-2xl bg-zinc-950 border border-white/15 sm:rounded shadow-[0_30px_80px_rgba(0,0,0,0.6)] max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
          <div className="flex items-center gap-3">
            <MessageCircleQuestion size={22} className="text-sky-400" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-sky-300 font-bold">
                Ask the customer
              </div>
              <div className="text-white font-display font-bold text-xl leading-tight">
                {lead?.name}
                <span className="text-zinc-400 font-medium text-sm ml-2">
                  · {lead?.service}
                </span>
              </div>
            </div>
          </div>
          <button
            data-testid="ask-questions-close"
            onClick={close}
            className="text-zinc-300 hover:text-white p-2 -mr-2"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6">
          <div>
            <div className="text-zinc-300 text-xs uppercase tracking-[0.18em] font-semibold mb-3">
              Quick prompts
            </div>
            <div className="flex flex-wrap gap-2">
              {PROMPTS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  data-testid={`ask-prompt-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => insertPrompt(p.text)}
                  className="text-xs uppercase tracking-[0.12em] font-bold border border-white/15 hover:border-sky-400 hover:text-sky-300 text-zinc-200 px-3 py-2 transition-colors"
                >
                  + {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="ask-message"
              className="block text-zinc-300 text-xs uppercase tracking-[0.18em] font-semibold mb-2"
            >
              Your message
            </label>
            <textarea
              id="ask-message"
              data-testid="ask-questions-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={9}
              placeholder="Hi! Before we send your tailored quote, could you confirm a few details…"
              className="w-full bg-zinc-900 border border-white/15 text-white text-sm leading-relaxed px-4 py-3 outline-none focus:border-sky-400 hover:border-white/30 transition-colors placeholder:text-zinc-500"
            />
            <div className="text-zinc-500 text-xs mt-1.5">
              The customer will reply to{" "}
              <span className="text-sky-400 font-medium">
                xavierserviceusa@gmail.com
              </span>
              . No price, no PDF, no Accept/Decline buttons are included.
            </div>
          </div>

          <div>
            <label
              htmlFor="ask-status"
              className="block text-zinc-300 text-xs uppercase tracking-[0.18em] font-semibold mb-2"
            >
              After sending, set lead status to
            </label>
            <select
              id="ask-status"
              data-testid="ask-questions-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full sm:w-64 bg-zinc-900 border border-white/15 text-white text-sm font-semibold uppercase tracking-[0.12em] py-2.5 px-3 outline-none focus:border-sky-400"
            >
              <option value="needs_info">needs_info</option>
              <option value="contacted">contacted</option>
            </select>
          </div>

          {error && (
            <div
              data-testid="ask-questions-error"
              className="bg-red-500/10 border border-red-500/40 text-red-300 px-4 py-3 text-sm"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              data-testid="ask-questions-success"
              className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 px-4 py-3 text-sm"
            >
              {success}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-white/10 px-6 py-4">
          <button
            data-testid="ask-questions-cancel"
            onClick={close}
            className="text-zinc-300 hover:text-white text-sm font-semibold uppercase tracking-[0.12em] px-4 py-2"
          >
            Cancel
          </button>
          <button
            data-testid="ask-questions-send"
            onClick={handleSend}
            disabled={sending || message.trim().length < 4}
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-sm uppercase tracking-[0.14em] font-bold px-5 py-2.5 transition-colors shadow-[0_4px_16px_rgba(14,165,233,0.35)]"
          >
            <Send size={14} />
            {sending ? "Sending…" : "Send question"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AskQuestionsDialog;
